import { useState, useEffect } from 'react';
import WizardLayout from './WizardLayout';
import { useWizard } from '../../contexts/WizardContext';
import { useProfile } from '../../contexts/ProfileContext';
import { createMemoriesFromWizardStep, createWizardMemories } from '../../utils/wizardMemoryTemplates';
import { authFetch } from '../../utils/auth';

export default function StepCurrentNeeds({ navigate, embedded = false, draft: externalDraft, setDraft: setExternalDraft }) {
  // Use either external draft (for embedded mode) or context draft (for wizard mode)
  const wizardContext = useWizard();
  const { profile, refresh } = useProfile();
  
  const draft = embedded ? externalDraft : wizardContext.draft;
  const setDraft = embedded ? setExternalDraft : wizardContext.setDraft;
  
  const [currentStruggles, setCurrentStruggles] = useState(draft?.currentStruggles || '');
  const [familyGoals, setFamilyGoals] = useState(draft?.familyGoals || '');
  const [needsIntensiveSupport, setNeedsIntensiveSupport] = useState(draft?.needsIntensiveSupport || false);
  const [needsNightWatch, setNeedsNightWatch] = useState(draft?.needsNightWatch || false);
  const [needsContinuousSupervision, setNeedsContinuousSupervision] = useState(draft?.needsContinuousSupervision || false);
  const [, setSavedMemories] = useState([]);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  
  // Keep local state in sync with draft changes (important for embedded mode)
  useEffect(() => {
    if (draft) {
      setCurrentStruggles(draft.currentStruggles || '');
      setFamilyGoals(draft.familyGoals || '');
      setNeedsIntensiveSupport(draft.needsIntensiveSupport || false);
      setNeedsNightWatch(draft.needsNightWatch || false);
      setNeedsContinuousSupervision(draft.needsContinuousSupervision || false);
    }
  }, [draft]);

  const skipWizard = async () => {
    try {
      // Create memories if user has provided any current needs info
      if (currentStruggles || familyGoals || needsIntensiveSupport) {
        const stepData = {
          currentStruggles,
          familyGoals,
          needsIntensiveSupport,
          needsNightWatch,
          needsContinuousSupervision,
          // Include child info from previous steps for entity mapping
          alias: draft?.child?.alias || '',
          birthYear: draft?.child?.birthYear || null
        };
        
        const memories = [];
        if (currentStruggles || familyGoals) {
          memories.push(...createMemoriesFromWizardStep('current_needs', stepData));
        }
        if (needsIntensiveSupport || needsNightWatch || needsContinuousSupervision) {
          memories.push(...createMemoriesFromWizardStep('intensive_needs', stepData));
        }
        
        if (memories.length > 0) {
          const result = await createWizardMemories(memories);
          if (result.success) {
            setSavedMemories(memories);
            setShowSaveConfirmation(true);
            setTimeout(() => setShowSaveConfirmation(false), 3000);
          }
        }
      }
      
      // IMPORTANT: Save skip tracking to profile
      await authFetch('/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...profile,
          onboardingSkipped: true,
          profileComplete: true,
          lastOnboardingStep: 4 // Track that user was on step 4 when they skipped
        })
      });
      
      // Wait until shared context is up-to-date
      await refresh();
      
      // Guard now sees correct flags
      navigate('/home', { replace: true });
    } catch (error) {
      console.error('Error skipping wizard:', error);
      alert('Det oppstod en feil. Vennligst prøv igjen.');
    }
  };

  const onNext = async ({ skip } = {}) => {
    if (embedded) return;
    
    if (skip) {
      await skipWizard();
      return;
    }
    
    try {
      // Create memories from current step data
      const stepData = {
        currentStruggles,
        familyGoals,
        needsIntensiveSupport,
        needsNightWatch,
        needsContinuousSupervision,
        // Include child info from previous steps for entity mapping
        alias: draft?.child?.alias || '',
        birthYear: draft?.child?.birthYear || null
      };
      
      const memories = [];
      if (currentStruggles || familyGoals) {
        memories.push(...createMemoriesFromWizardStep('current_needs', stepData));
      }
      if (needsIntensiveSupport || needsNightWatch || needsContinuousSupervision) {
        memories.push(...createMemoriesFromWizardStep('intensive_needs', stepData));
      }
      
      if (memories.length > 0) {
        const result = await createWizardMemories(memories);
        if (result.success) {
          setSavedMemories(memories);
          setShowSaveConfirmation(true);
          setTimeout(() => setShowSaveConfirmation(false), 3000);
        }
      }
      
      // Update local wizard state for flow continuity
      setDraft({
        ...draft,
        currentStruggles,
        familyGoals,
        needsIntensiveSupport,
        needsNightWatch,
        needsContinuousSupervision
      });
      
      navigate('/onboarding/benefits');
    } catch (error) {
      console.error('Error in onNext:', error);
      alert('Det oppstod en feil. Vennligst prøv igjen.');
    }
  };

  // Check if child has autism for intensive support relevance
  const hasAutism = draft?.child?.diagnoses?.some(d => 
    d.toLowerCase().includes('autisme') || d.toLowerCase().includes('autism')
  );

  // The form content
  const formContent = (
    <>
      <h2 className="text-xl font-semibold mb-6">Hva er situasjonen deres akkurat nå?</h2>
      

      {/* Save confirmation */}
      {showSaveConfirmation && (
        <div className="mb-4 p-3 bg-sage-50 border border-sage-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-sage-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sage-800 font-medium">Lagret informasjon om nåværende situasjon</span>
          </div>
        </div>
      )}

      <label className="block mb-6">
        <span className="text-base font-medium mb-2 block">
          Hva strever dere mest med i hverdagen rundt {draft?.child?.alias || 'barnet'} akkurat nå?
        </span>
        <textarea 
          value={currentStruggles} 
          onChange={e => setCurrentStruggles(e.target.value)} 
          className="w-full p-3 border rounded-lg mt-1 h-24 resize-none"
          placeholder="F.eks. søvnproblemer, utfordrende atferd, manglende struktur..."
        />
        <p className="text-xs text-text-muted mt-1">Valgfritt, men hjelper oss å gi bedre råd</p>
      </label>

      <label className="block mb-6">
        <span className="text-base font-medium mb-2 block">
          Hva drømmer dere om / skulle ønske det var i stedet?
        </span>
        <textarea 
          value={familyGoals} 
          onChange={e => setFamilyGoals(e.target.value)} 
          className="w-full p-3 border rounded-lg mt-1 h-24 resize-none"
          placeholder="F.eks. mer ro i hverdagen, bedre søvn, at barnet trives bedre på skolen..."
        />
        <p className="text-xs text-text-muted mt-1">Valgfritt, men hjelper oss å forstå målene deres</p>
      </label>

      {/* Intensive support needs - especially relevant for autism */}
      {hasAutism && (
        <div className="mb-6 p-4 bg-warning-50 border border-warning-200 rounded-lg">
          <h3 className="font-medium mb-3">Omfattende hjelpebehov</h3>
          <p className="text-sm text-text-secondary mb-4">
            For barn med autisme kan det være aktuelt med mer omfattende støtteordninger som pleiepenger eller BPA.
          </p>
          
          <div className="space-y-3">
            <label className="flex items-start">
              <input
                type="checkbox"
                checked={needsNightWatch}
                onChange={e => setNeedsNightWatch(e.target.checked)}
                className="mt-1 mr-3"
              />
              <span>Trenger nattevåk eller tilsyn om natten</span>
            </label>
            
            <label className="flex items-start">
              <input
                type="checkbox"
                checked={needsContinuousSupervision}
                onChange={e => setNeedsContinuousSupervision(e.target.checked)}
                className="mt-1 mr-3"
              />
              <span>Trenger kontinuerlig tilsyn gjennom store deler av dagen</span>
            </label>
            
            <label className="flex items-start">
              <input
                type="checkbox"
                checked={needsIntensiveSupport}
                onChange={e => setNeedsIntensiveSupport(e.target.checked)}
                className="mt-1 mr-3"
              />
              <span>Har omfattende hjelpebehov i dagliglivet</span>
            </label>
          </div>
        </div>
      )}

    </>
  );

  // For embedded mode, just return the form content
  if (embedded) {
    return (
      <div className="border-b pb-8">
        {formContent}
      </div>
    );
  }

  // For wizard mode, wrap in WizardLayout
  return (
    <WizardLayout
      step={4}
      total={5}
      onNext={onNext}
    >
      {formContent}
    </WizardLayout>
  );
}